import {
  TConvertKeys,
  ELanguage,
  TNullable,
  TDotNotation,
  TDeepRequired,
  IAddressFields,
} from './common';
import { IPeriod } from './date';
import { EPaymentGateway } from './payments';
import { IPhone, TUserDataCamel } from './user';

export interface IInstitutionResponse {
  data: { data: IInstitutionServer };
}

export interface IAllInstitutionsResponse {
  data: {
    data: IInstitutionServer[];
  };
}

export enum EImageShape {
  ROUND = 'round',
  SQUARE = 'square',
}

export enum EMessagingChannel {
  SMS = 'sms',
  WHATSAPP = 'whatsapp',
}

export enum ESymptomTriage {
  DISABLED = 'disabled',
  MANUAL = 'manual',
  INTERACTIVE = 'interactive',
}

export interface IInstitutionContractServer {
  id?: string;
  seats?: number;
  station_licenses?: number;
  contract_start_date?: string;
  contract_end_date?: string;
}

export interface IInstitutionProvisioningServer {
  contracts?: IInstitutionContractServer[];
  seats?: TNullable<number>;
  occupied_seats?: TNullable<number>;
}

export interface IPocSettingsServer {
  city: string;
  name: string;
  email: string;
  phone: IPhone;
  state: TNullable<string>;
  title: string;
  address1: string;
  address2: string;
  country: TNullable<string>;
  zip: string;
}

interface IInstitutionDosespotServer {
  clinic_id?: number;
  clinic_key?: string;
  enabled?: boolean;
}

type TFAConfiguration = {
  enabled: boolean;
};

export interface IInstitutionConfigurationServer {
  enable_chat_appointment_types: boolean;
  enforce_country: boolean;
  dispatcher_option: boolean;
  auto_confirm: boolean;
  waiting_room_option: boolean;
  symptom_triage?: ESymptomTriage;
  reping_times: number;
  reping_delay: number;
  dispatcher_timer: TNullable<number>;
  waiting_room_timer: TNullable<number>;
  required_patient_fields: TRequiredUserField[];
  required_provider_fields: TRequiredUserField[];
  dosespot?: TNullable<IInstitutionDosespotServer>;
  canary_speech?: boolean;
  tfa?: TFAConfiguration;
  kiosk_mode?: boolean;
  medical_station_calls?: boolean;
  disable_patient_scheduling?: boolean;
  advance_notice_buffer: number;
  medwand?: boolean;
  patient_data_retention_period?: number;
  mobile_messaging_channel?: EMessagingChannel;
}

export interface IWhiteLabelServer {
  enabled: boolean;
  product_name: string;
  subdomain: string;
  logo_image_url: TNullable<string>;
  logo_image_shape?: EImageShape;
  mobile_home_page_logo_url?: TNullable<string>;
  banner_image_url?: TNullable<string>;
  cover_image_url: TNullable<string>;
  schedule_image_url: string;
  on_demand_image_url: string;
  primary_color: string;
  schedule_background_color: string;
  schedule_text_color: string;
  schedule_highlight_one_color: string;
  schedule_highlight_two_color: string;
  is_solid_schedule: boolean;
  on_demand_background_color: string;
  on_demand_text_color: string;
  on_demand_highlight_one_color: string;
  on_demand_highlight_two_color: string;
  is_solid_on_demand: boolean;
  button_text_color: string;
  favicon_image_url: TNullable<string>;
  appointment_text_color: string;
  appointment_background_color: string;
  appointment_highlight_one_color: string;
  appointment_highlight_two_color: string;
  is_solid_appointment: boolean;
}

interface IInsuranceServer {
  id?: string;
  name: string;
  copay_value: number;
}

export interface IInsurancesSettingsServer {
  enabled: TNullable<boolean>;
  premium_enabled: TNullable<boolean>;
  premium_package_name: TNullable<string>;
  insurances: IInsuranceServer[];
}

interface IPaymentServerGateWay {
  type: EPaymentGateway;
  merchant_id: string;
  merchant_name: string;
  authorization_key: string;
  currency: string;
  active: boolean;
}

export interface IPaymentServer {
  schedule_reminder: number;
  intervals: number;
  enabled: boolean;
  enable_reminder?: boolean;
  gateways: IPaymentServerGateWay[];
}

export interface IServiceForm {
  template_id: string;
  required: boolean;
}

export interface IServiceServer {
  id?: string;
  name: string;
  description: string;
  fee_enabled?: boolean;
  fee: number;
  allow_encounter_now?: TNullable<boolean>;
  enabled?: boolean;
  specialty: string;
  user_institution_role_ids?: string[];
  appointment_duration: number[];
  forms: IServiceForm[];
  is_default?: boolean;
}

export type TPaymentServerGateWayCamel = TConvertKeys<IPaymentServerGateWay>;
export type TServiceServerCamel = TConvertKeys<IServiceServer>;

export interface IFormPacketsServer {
  id: string;
  name: string;
  form_ids: string[];
}

export interface IInstitutionKioskServer {
  id?: string;
  code?: string;
  enabled: boolean;
  name: string;
  location: string;
  timezone: string;
  schedule?: IPeriod[];
  is_autotimezone?: boolean;
}

export type TInstitutionKioskServerCamel =
  TConvertKeys<IInstitutionKioskServer>;

export interface IInstitutionServer extends IAddressFields {
  configuration: TNullable<Partial<IInstitutionConfigurationServer>>;
  id: string;
  insurance: TNullable<Partial<IInsurancesSettingsServer>>;
  name: string;
  language: ELanguage;
  payment: Partial<IPaymentServer>;
  phone: TNullable<IPhone>;
  poc_settings: Partial<IPocSettingsServer>;
  self_enroll_access_code: string;
  services: IServiceServer[];
  whitelabel: TNullable<Partial<IWhiteLabelServer>>;
  updated_at: string;
  form_packets: IFormPacketsServer[];
  currency?: string;
  kiosks?: IInstitutionKioskServer[];
  provisioning?: Partial<IInstitutionProvisioningServer>;
  organization_id: string;
}

export type IInstitutionNotSignedServer = Pick<
  IInstitutionServer,
  'id' | 'language' | 'whitelabel' | 'self_enroll_access_code'
>;

export type TPatientSex = 'male' | 'female';

export interface IInstitutionPatientRequest {
  first_name: string;
  last_name: string;
  /** YYYY-MM-DD; a UTC date-time works too, the backend drops its time. */
  dob: string;
  gender: TPatientSex;
  /** Must not belong to any other user. */
  phone: IPhone;
  /** Without it the account gets a placeholder address. */
  email?: string;
}

export type TInstitutionPatientRequestCamel =
  TConvertKeys<IInstitutionPatientRequest>;

export type TInstitutionServerCamel = TConvertKeys<IInstitutionServer>;
export type TInstitutionNotSignedCamel =
  TConvertKeys<IInstitutionNotSignedServer>;
export type TFormPacketsServerCamel = TConvertKeys<IFormPacketsServer>;
export type TServiceFormCamel = TConvertKeys<IServiceForm>;

export type TRequiredUserField = TDotNotation<TDeepRequired<TUserDataCamel>>;
export type TInstitutionWhiteLabelServerCamel = TConvertKeys<IWhiteLabelServer>;
